import { createContext, useContext, useMemo, useState } from 'react';

export type ChromeMode = 'default' | 'workspace';

interface ShellContextValue {
  /** Element the topbar renders page context into. Pages portal into it. */
  topbarSlot: HTMLElement | null;
  setTopbarSlot: (element: HTMLElement | null) => void;
  /**
   * 'workspace' collapses the sidebar and hands scrolling to the page — the editor needs the
   * full viewport and manages its own panes. It is still the same shell, same topbar chrome.
   */
  chromeMode: ChromeMode;
  setChromeMode: (mode: ChromeMode) => void;
}

const ShellContext = createContext<ShellContextValue | null>(null);

export function ShellProvider({ children }: { children: React.ReactNode }) {
  const [topbarSlot, setTopbarSlot] = useState<HTMLElement | null>(null);
  const [chromeMode, setChromeMode] = useState<ChromeMode>('default');

  const value = useMemo(
    () => ({ topbarSlot, setTopbarSlot, chromeMode, setChromeMode }),
    [topbarSlot, chromeMode],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellContextValue {
  const context = useContext(ShellContext);
  if (!context) throw new Error('useShell must be used inside <ShellProvider>');
  return context;
}
